import type { PayloadAction } from '@reduxjs/toolkit';
import {
  findNodes,
  withSuggestions,
} from '../../../../components/Tree/helpers/helpers';
import { getCyclicIndex } from '../../../../utils/array/array';
import { getCatalogId } from '../../../../utils/tree/ids';
import type { TreeCatalogs } from '../index';
import type { NodeBaseItem } from '../../../../entities/Node';

interface HighlightAction {
  highlight?: boolean;
  highlightItem?: '+' | '-' | '';
  categoryIds?: string[];
}

interface TreeState {
  catalogs: { [key: string]: TreeCatalogs | Record<string, never> };
  [key: string]: unknown;
}

export function highlightSuggestedCategoriesReducer(
  state: TreeState,
  action: PayloadAction<HighlightAction, string, { tree: string }>,
): void {
  const {
    meta: { tree },
    payload: { highlight = true, highlightItem = '', categoryIds = [] },
  } = action;
  const catalogId = getCatalogId(tree);
  const treeData = state.catalogs[catalogId]?.treeData || [];
  const searchMethod = highlight
    ? (ni: NodeBaseItem) => categoryIds.includes(ni.node.categoryId)
    : () => false;
  // @ts-expect-error - findNodes returns complex structure
  const result: { matches: NodeBaseItem[] } = findNodes({
    treeData,
    searchMethod,
    searchFocusOffset: 0,
    searchQuery: null,
  });
  const matches = result.matches;
  const idx = getHighlightIndex(state.catalogs[catalogId], highlightItem, matches);
  const highlightFocusId = matches[idx]?.node.id;

  state.catalogs[catalogId].treeData = withSuggestions({
    treeData,
    matches,
    focusId: highlightFocusId,
    expandMatches: true,
  });
  state.catalogs[catalogId].highlightIndex = highlight ? String(idx) : '';
  state.catalogs[catalogId].highlightFocusId = highlight ? highlightFocusId : '';
}

function getHighlightIndex(
  currentState: TreeCatalogs | Record<string, never>,
  highlightItem: string,
  matches: NodeBaseItem[],
): number {
  if (!matches.length) {
    return 0;
  }

  const currentIndex = (currentState as TreeCatalogs)?.highlightIndex;
  let highlightIndex = currentIndex ? parseInt(currentIndex, 10) : -1;

  if (highlightItem === '+') {
    highlightIndex += 1;
  } else if (highlightItem === '-') {
    highlightIndex -= 1;
  } else {
    highlightIndex = 0;
  }

  const idx = getCyclicIndex(matches, highlightIndex);
  return idx;
}
